import './AuthPage.scss';

import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Input, message, Modal, Row } from 'antd';
import { checkIfUserExists, loginUsingPassword, triggerEmailLoginFlow } from '../../api/user';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { loginAndRedirect } from '../../common/auth';
import logoImage from '../../assets/images/logo/favicon.svg';
import { MailOutlined } from '@ant-design/icons';
import RoutePaths from '../../router/RoutePaths';
import { AuthOptions } from '../../types/AuthOptions';
import CustomInputLabel from '../../components/UI/CustomInputLabel';
import { OrDivider } from '../../components/UI/AuthButton/OrDivider';
import { GoogleButton } from '../../components/UI/AuthButton/GoogleButton';
import { getAuthorisationURLWithQueryParamsAndSetState } from 'supertokens-web-js/recipe/thirdparty';
import { getWebsiteDomain } from '../../authConfig';
import { handleApiError } from '../../utils/errorHandling';
import { ValidationError } from '../../utils/errorHandling';
import { useAuthStore } from '../../stores/authStore';
import { MagicLinkRedirectState } from '../../common/signUpMethod';

type OnFinishStructure = {
  email: string;
  password: string;
};

const STEP_INTERVAL = 1000;
const MAX_PROGRESS = 60;
const MIN_PROGRESS = 0;

export default function Login() {
  const [magicLinkForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const { email, setEmail } = useAuthStore();
  const [resendTriggeredTimeout, setResendTriggeredTimeout] = useState(MAX_PROGRESS);
  const [isIntervalRunning, setIsIntervalRunning] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeAuthMode, setActiveAuthMode] = useState(AuthOptions.MagicLink);
  // Google refused a magic link account and sent the user here, so the link went out on its own.
  const [magicLinkRedirected, setMagicLinkRedirected] = useState(false);
  const magicLinkRedirectHandled = useRef(false);

  useEffect(() => {
    let interval: ReturnType<typeof window.setInterval>;

    if (isIntervalRunning && resendTriggeredTimeout > MIN_PROGRESS) {
      interval = setInterval(() => {
        setResendTriggeredTimeout((prev) => prev - 1);
      }, STEP_INTERVAL);
    }

    return () => clearInterval(interval);
  }, [isIntervalRunning, resendTriggeredTimeout]);

  useEffect(() => {
    const linkAuthMode = searchParams.get('mode');
    if (linkAuthMode && linkAuthMode === 'password') {
      setActiveAuthMode(AuthOptions.Password);
    } else if (linkAuthMode && linkAuthMode === 'magicLink') {
      setActiveAuthMode(AuthOptions.MagicLink);
    }
  }, []);

  const resetCounter = () => {
    setIsIntervalRunning(true);
    setResendTriggeredTimeout(MAX_PROGRESS);
  };

  const onFinish = async (values: OnFinishStructure) => {
    try {
      const res = await triggerEmailLoginFlow(values.email);
      // A Google account gets no link; the Google button below the form is the way in.
      if (res.status === 'ACCOUNT_USES_GOOGLE') {
        void message.error('This account was created with Google. Please continue with Google.');
        return;
      }

      showModal();

      if (!isIntervalRunning) {
        resetCounter();
      }
    } catch (error: any) {
      if (
        error.response?.status === 404 ||
        (error.response?.status === 400 && error.response?.data?.code === 'ERR-USER-20')
      ) {
        message.error('A user with this email does not exist. Please try signing up instead.');
      } else {
        handleApiError(error);
      }
    }
  };

  useEffect(() => {
    const redirectEmail = (location.state as MagicLinkRedirectState | null)?.magicLinkRedirectEmail;
    if (!redirectEmail || magicLinkRedirectHandled.current) {
      return;
    }

    magicLinkRedirectHandled.current = true;
    // Drop the state so a refresh does not send another link.
    navigate(location.pathname, { replace: true, state: null });
    setActiveAuthMode(AuthOptions.MagicLink);
    setEmail(redirectEmail);
    setMagicLinkRedirected(true);
    void onFinish({ email: redirectEmail, password: '' });
  }, []);

  const resendRequest = async (email: string) => {
    try {
      setModalLoading(true);
      await triggerEmailLoginFlow(email);
      setModalLoading(false);
      resetCounter();
    } catch (error) {
      setModalLoading(false);
      handleApiError(error, 'Failed to resend login email. Please try again.');
    }
  };

  const onFinishFailed = (errorInfo: unknown) => {
    handleApiError(new ValidationError('Form validation failed', 'FORM_ERROR', errorInfo));
  };

  const [modalLoading, setModalLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const showModal = () => {
    setOpenModal(true);
  };

  // const handleOk = () => {
  //   setModalLoading(true);
  //   setTimeout(() => {
  //     setModalLoading(false);
  //     setOpenModal(false);
  //   }, 3000);
  // };

  const handleCancel = () => {
    setOpenModal(false);
    setMagicLinkRedirected(false);
  };

  const [loginErrorMessages, setLoginErrorMessages] = useState<Record<string, string>[]>([]);

  const [messageApi, contextHolder] = message.useMessage();

  const onFinishPassword = async (values: OnFinishStructure) => {
    try {
      setLoginErrorMessages([]);

      // Check if user exists before attempting login
      await checkIfUserExists(values.email);

      const res = await loginUsingPassword({
        email: values.email,
        password: values.password,
      });

      void messageApi.open({
        type: 'success',
        content: 'Logging in...',
      });

      void loginAndRedirect(res.data);
      setEmail('');
    } catch (e: any) {
      const newErrors = [];

      if (
        e.response?.status === 404 ||
        (e.response?.status === 400 && e.response?.data?.code === 'ERR-USER-20')
      ) {
        newErrors.push({
          message: 'User not found',
          description: 'A user with this email does not exist. Please try signing up instead.',
        });
      } else if (e.response?.status === 401) {
        newErrors.push({
          message: 'Invalid credentials',
          description: 'Please check your email and password and try again',
        });
      } else {
        newErrors.push({
          message: 'Login failed',
          description: 'Something went wrong. Please try again.',
        });
      }

      setLoginErrorMessages([...newErrors]);
    }
  };

  const onGoogleSignUp = async () => {
    try {
      const authUrl = await getAuthorisationURLWithQueryParamsAndSetState({
        thirdPartyId: 'google',
        frontendRedirectURI: `${getWebsiteDomain()}/auth/callback/google`,
      });
      // we redirect the user to google for auth.
      window.location.assign(authUrl);
    } catch (err: any) {
      if (err.isSuperTokensGeneralError === true) {
        // this may be a custom error message sent from the API by you.
        void message.error(err.message);
      } else {
        void message.error('Oops! Something went wrong.');
      }
    }
  };

  const AuthForm = ({ mode }: { mode: AuthOptions }) => {
    if (mode === AuthOptions.MagicLink) {
      return (
        <Form
          form={magicLinkForm}
          name="login-form"
          initialValues={{ remember: true, email }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          // Clear the email error as soon as the user resumes typing, so the validation
          // message goes away until the next blur or submit (ENTER). #955
          onValuesChange={(changed) => {
            if ('email' in changed) magicLinkForm.setFields([{ name: 'email', errors: [] }]);
          }}
          className="signup-form w-[320px]"
        >
          <CustomInputLabel>Email</CustomInputLabel>

          <Form.Item
            name="email"
            // Validate on blur (and on submit), not on every keystroke, so the
            // "not a valid email" error doesn't flash on the first character. #955
            validateTrigger="onBlur"
            rules={[
              {
                required: true,
                message: 'Please input your email!',
              },
              {
                type: 'email',
                message: 'Email is not valid',
              },
            ]}
            wrapperCol={{ sm: 24 }}
          >
            <Input placeholder="Enter your work email" className="h-12" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              shape="round"
              className="auth-submit-button text-base text-brand-white font-figtree font-semibold"
              block
            >
              Login with Magic link
            </Button>
          </Form.Item>
        </Form>

        // </Col>
      );
    } else if (mode === AuthOptions.Password) {
      return (
        // <Col span={8}>
        <Form
          form={passwordForm}
          name="login-form-password"
          labelCol={{ span: 8 }}
          // wrapperCol={{ span: 16 }}
          initialValues={{ remember: true, email }}
          onFinish={onFinishPassword}
          onFinishFailed={onFinishFailed}
          // Clear the email error as soon as the user resumes typing, so the validation
          // message goes away until the next blur or submit (ENTER). #955
          onValuesChange={(changed) => {
            if ('email' in changed) passwordForm.setFields([{ name: 'email', errors: [] }]);
          }}
          className="w-[320px]"
        >
          <CustomInputLabel>Email</CustomInputLabel>

          <Form.Item
            name="email"
            // Validate on blur (and on submit), not on every keystroke, so the
            // "not a valid email" error doesn't flash on the first character. #955
            validateTrigger="onBlur"
            rules={[
              {
                required: true,
                message: 'Please input your email!',
              },
              {
                type: 'email',
                message: 'Email is not valid',
              },
            ]}
            wrapperCol={{ sm: 24 }}
          >
            <Input placeholder="Enter your work email" className="h-12" />
          </Form.Item>
          <CustomInputLabel>Password</CustomInputLabel>

          <Form.Item
            name="password"
            rules={[
              {
                required: true,
                message: 'Please input your password!',
              },
            ]}
            wrapperCol={{ sm: 24 }}
          >
            <Input placeholder="Insert your password" className="h-12" type={'password'} />
          </Form.Item>
          <Form.Item className="w-[320px] mt-10">
            <Button type="primary" htmlType="submit" size="large" shape="round" className="auth-submit-button" block>
              Login
            </Button>
          </Form.Item>

          <div className="w-full h-5 flex items-center justify-around gap-2 mt-10">
            <div className="h-[1px] bg-[#DEE0EB] flex-1"></div>
            <div className=" text-center text-slate-500 text-base font-normal ">Or</div>
            <div className="h-[1px] bg-[#DEE0EB] flex-1"></div>
          </div>

          <Button
            type="default"
            htmlType="submit"
            size="large"
            shape="round"
            className="mt-10"
            block
            onClick={() => {
              setActiveAuthMode(AuthOptions.MagicLink);
            }}
          >
            <MailOutlined style={{ marginRight: '8px' }} /> Log in with email link
          </Button>
        </Form>
        // </Col>;
      );
    }
    return <></>;
  };

  return (
    <div>
      <div className="auth-page-wrapper">
        {contextHolder}

        <h2 className="logo-image text-center flex align-middle justify-center">
          <img src={logoImage} alt="Logo" style={{ height: '2rem', marginRight: '0.25rem' }} /> Taskonio
        </h2>
        <h1 className="mb-8">Log in to Taskonio </h1>

        <Row justify={'center'} align={'middle'} className={'mb-6'}>
          {(() => {
            if (loginErrorMessages.length > 0) {
              return (
                <>
                  {loginErrorMessages.map((error, i) => {
                    return (
                      <Alert
                        key={i}
                        message={error.message}
                        description={error.description}
                        type="error"
                        showIcon
                        closable
                      />
                    );
                  })}
                </>
              );
            } else {
              return <></>;
            }
          })()}
        </Row>

        <Row justify={'center'} align={'middle'}>
          <div className="flex-col flex justify-stretch items-stretch">
            <AuthForm mode={activeAuthMode} />
            <OrDivider />
            <GoogleButton onClick={onGoogleSignUp} />
          </div>
        </Row>
        <div className="text-base text-brand-grey-dark font-figtree font-semibold flex flex-col justify-center items-center mt-16">
          Don&apos;t have an account?
          <Button
            className="text-base text-blue-600 font-figtree font-semibold py-2 px-1"
            type="link"
            onClick={() => {
              const emailValue = magicLinkForm.getFieldValue('email') || passwordForm.getFieldValue('email');
              if (emailValue) setEmail(emailValue);
              navigate(RoutePaths.Signup);
            }}
          >
            Sign up here
          </Button>
        </div>

        <Modal
          open={openModal}
          // onOk={handleOk}
          onCancel={handleCancel}
          footer={null}
        >
          <div className="flex flex-col items-center">
            <div className="text-center text-brand-grey-dark text-[32px] font-semibold font-['Figtree'] ">
              Check your email
            </div>

            <div className="w-96 text-center mt-3">
              <span className="text-brand-grey-dark text-base font-normal font-['Figtree']">
                We've sent you a temporary login link to{' '}
              </span>
              <span className="text-brand-grey-dark text-base font-semibold font-['Figtree']">{email}</span>
            </div>
            {magicLinkRedirected && (
              <div className="w-96 text-center text-brand-grey-dark text-base font-normal font-['Figtree'] mt-3">
                This account was created with an email login link, so we sent you one instead of logging you in with
                Google.
              </div>
            )}
            <div className="w-96 text-center text-brand-grey-dark text-base font-normal font-['Figtree'] mt-3">
              Please check your email inbox and spam folder to make sure you receive it.
            </div>
            <svg
              className="mt-10 mb-10"
              xmlns="http://www.w3.org/2000/svg"
              width="91"
              height="60"
              viewBox="0 0 91 60"
              fill="none"
            >
              <rect width="90.3614" height="60" rx="5.78313" fill="#08CF65" />
              <path
                d="M50.1759 34.0348L88.0335 9.29494C92.3478 6.47561 90.2685 0 85.0491 0H5.31228C0.0725292 0 -1.99233 6.51672 2.36042 9.31608L40.8474 34.068C43.6726 35.885 47.3649 35.8718 50.1759 34.0348Z"
                fill="#1B4349"
              />
            </svg>
            <div className="text-center text-brand-grey-dark text-base font-semibold font-['Figtree']">
              Haven't gotten the email?
            </div>
            <div className="text-center mt-[-4px]">
              {resendTriggeredTimeout > 0 ? (
                <p>You can send next request in {resendTriggeredTimeout} seconds.</p>
              ) : (
                <>
                  <Button
                    type="link"
                    className="p-0 m-0"
                    onClick={async () => {
                      await resendRequest(email);
                    }}
                  >
                    <span className=" text-base font-medium font-['Figtree'] underline">Resend the link</span>
                  </Button>
                </>
              )}
            </div>
            <Button type="text" block className="mt-10" onClick={handleCancel}>
              <div className="text-center text-brand-grey-dark text-base font-semibold font-['Figtree']">Back to login</div>
            </Button>
          </div>
        </Modal>
      </div>
    </div>
  );
}
